import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useSettingsVM, { OrganisationSettingMap } from './useSettingsVM';
import useUpdateSettings from './useUpdateSettings';
import useContentRepositoriesByOrgAPI from './useContentRepositoriesByOrgAPI';
import { OrganisationSettingVM } from '../../../apollo/queries/organisations/organisationSetting.queries';
import appLocalStorage from '../../../utils/AppLocalStorage';

interface UseSettingReturnType {
  isLoading: boolean;
  isFormValid: boolean;
  settings: OrganisationSettingMap;
  onUpdate: (setting: OrganisationSettingVM) => void;
  isViewOnlyRole: boolean;
  onSaveChanges: () => void;
}

const useSettings = (): UseSettingReturnType => {
  const { code } = useParams<{ code: string }>();
  const { loading: isCRbyOrgLoading, contentRepositories } =
    useContentRepositoriesByOrgAPI(code);

  const settingsVMData = useSettingsVM(contentRepositories);

  const [settings, setSettings] =
    useState<OrganisationSettingMap>(settingsVMData);
  const { loading: isUpdating, onUpdateSettings } = useUpdateSettings();

  useEffect(() => {
    setSettings(settingsVMData);
  }, [isCRbyOrgLoading]);

  const onUpdate = (setting: OrganisationSettingVM) => {
    setSettings((prevSettings) => {
      const oldSettings = new Map(prevSettings);
      const settingsToUpdate = oldSettings.get(setting.category) || [];
      const updatedSettings = settingsToUpdate.map((s) =>
        s.settingCode === setting.settingCode ? setting : s,
      );
      return oldSettings.set(setting.category, updatedSettings);
    });
  };

  const getAllSettings = (): OrganisationSettingVM[] => {
    const allSettings: OrganisationSettingVM[] = [];
    settings.forEach((items) => allSettings.push(...items));
    return allSettings;
  };

  const onSaveChanges = () => onUpdateSettings(settings, code);

  const getIsFormValid = (): boolean => {
    const hasEmptyValue = getAllSettings().some((s) => {
      if (typeof s.value === 'string') {
        return s.value.trim() === '';
      }
      return false;
    });

    return !hasEmptyValue && !isUpdating;
  };

  return {
    isLoading: isCRbyOrgLoading,
    settings,
    isFormValid: getIsFormValid(),
    onUpdate,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    onSaveChanges,
  };
};

export default useSettings;
